import { ReactElement, useContext, useEffect, useMemo } from 'react';
import { useDynamicContext } from '@dynamic-labs/sdk-react-core';
import Stack from '@mui/material/Stack';
import { useTheme, SxProps } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';

// Assets
import { ReactComponent as MapleIsotype } from 'Components/ds/assets/icon-maple-isotype.svg';

// Components
import MapleCard from 'Components/ds/MapleCard';
import MapleCardContent from 'Components/ds/MapleCardContent';
import Typography from 'Components/ds/Typography';
import MapleLink from 'Components/ds/MapleLink';
import DynamicWidget from 'Pages/SignupLogin/DynamicWidget';

// Context
import { UserDataContext } from 'Context/UserData/UserData';

// Styles
import { shadow } from 'Components/ds/MapleTheme/designSystem';

type SignupLoginFormProps = {
  formType: 'signup' | 'login';
};

const styles: { [key: string]: SxProps } = {
  logo: {
    width: '60px',
    height: '60px',
    boxShadow: shadow.small,
    borderRadius: '30px',
  },
  message: { transform: 'translateY(-10px)' },
  link: { weight: 'bold' },
};

const SignupLoginForm = ({ formType }: SignupLoginFormProps): ReactElement => {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { isVerificationInProgress, isAuthenticated } = useDynamicContext();
  const { setLoginInitiated } = useContext(UserDataContext);

  useEffect(() => {
    if (!isAuthenticated) return;

    const queryParams = new URLSearchParams(search);
    const redirectParam = queryParams.get('redirect');

    setLoginInitiated(true);

    if (redirectParam) return navigate(redirectParam);

    return navigate('/v2/lend/dashboard', { replace: true });
  }, [search, isAuthenticated]);

  const title = useMemo((): string => {
    if (isVerificationInProgress) return 'Verify your email';
    if (formType === 'signup') return 'Create your Maple account';

    return 'Welcome back';
  }, [isVerificationInProgress, formType]);

  return (
    <MapleCard sx={{ position: 'relative' }}>
      <MapleCardContent>
        <Stack spacing={3}>
          <Stack spacing={3} alignItems='center'>
            <Stack sx={styles.logo} alignItems='center' justifyContent='center'>
              <MapleIsotype fill={palette.ds.primary[500]} />
            </Stack>

            <Typography variant='h4' textAlign='center' weight='extraBold'>
              {title}
            </Typography>
            {formType === 'signup' && !isVerificationInProgress && (
              <Typography variant='paragraphSmall' color='ds.neutral.600' align='center'>
                Create your account to access consistent high yield in DeFi
              </Typography>
            )}
          </Stack>
          <DynamicWidget />
          <Stack spacing={1}>
            {formType === 'signup' && (
              <Typography variant='paragraphXSmall' color='ds.neutral.600' align='center' sx={styles.message}>
                Already have an account?{' '}
                <MapleLink href={`/#/login${search}`} variant='paragraphXSmall' underline='none' sx={styles.link}>
                  Log in
                </MapleLink>
              </Typography>
            )}
            {formType === 'login' && (
              <Typography variant='paragraphXSmall' color='ds.neutral.600' align='center' sx={styles.message}>
                Don't have an account?{' '}
                <MapleLink href={`/#/signup${search}`} variant='paragraphXSmall' underline='none' sx={styles.link}>
                  Sign Up
                </MapleLink>
              </Typography>
            )}
            <Typography variant='paragraphXSmall' color='ds.neutral.600' align='center'>
              By continuing, you agree to{' '}
              <MapleLink
                href='https://maplefinance.gitbook.io/maple/legal/interface-terms-of-use'
                target='_blank'
                rel='noreferrer'
                variant='paragraphXSmall'
                link='tertiary'
              >
                Maple Terms of Service
              </MapleLink>{' '}
              and{' '}
              <MapleLink
                href='https://maplefinance.gitbook.io/maple/legal/privacy-policy'
                target='_blank'
                rel='noreferrer'
                variant='paragraphXSmall'
                color='ds.neutral.500'
                link='tertiary'
              >
                Privacy Policy
              </MapleLink>
              .
            </Typography>
          </Stack>
        </Stack>
      </MapleCardContent>
    </MapleCard>
  );
};

export default SignupLoginForm;
